// InlineRecipeEditorV2 — primary working surface for a source's data.
//
// User vision (2026-05-09): "sljedece sto zelim kao user vidjeti nakon
// sto sam otvorio source je edit tog sourca kroz neki tablici pregled
// koji je direktno kroz bq i koji sve sto se sejva u njemu napravi kao
// view a istu logiku moze koristiti kroz stream."
//
// Operator opens a source → lands directly on the data (rows from the
// `events` view, queried live through BQ). Above the table, a recipe
// toolbar: select a base view, add filter rows, set unique-on-key,
// pick a stream-time action. Live preview rebuilds as the recipe
// changes. "Save as view" creates a BQ view (always) + optional
// trigger (validate / dedupe / forward / enrich) — same recipe drives
// both query-time analytics AND ingest-time per-event processing.
//
// This is the inline counterpart to RecipeBuilderModal — same recipe
// shape, same SQL helpers, same trigger creation logic. The modal is
// kept as a focused "+ New view" CTA from the views list; this
// inline editor is the default surface when the BQ tab is open.

import React, { useEffect, useMemo, useState } from 'react';
import type { TriggerAction } from '@datahub/shared';
import {
  createBqView,
  createTrigger,
  createView,
  getEventsSchemaFlat,
  previewBqView,
  type BqView,
  type SchemaField,
} from '../../api';
import { useToast } from '../Toast';
import { buildRecipeSql, filterRowsToTree, type FilterRow } from './recipe-sql';

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  fontFamily: 'var(--mono)',
  fontSize: 12,
  border: '1px solid var(--line)',
  borderRadius: 4,
  background: 'var(--bg)',
  color: 'var(--ink)',
};

type StreamAction = 'none' | 'validate' | 'dedupe' | 'forward' | 'enrich';

export function InlineRecipeEditorV2({ sourceId, dataset, views, onSaved }: {
  sourceId: string;
  dataset: string;
  views: BqView[];
  onSaved: () => void;
}): React.ReactElement {
  const toast = useToast();

  // Base view that filters/recipes compose on top of. Defaults to the
  // platform's auto-created `events` view; operator can swap to any
  // existing saved view to refine an existing slice further.
  const [baseView, setBaseView] = useState<string>(() => views.find(v => v.name === 'events')?.name ?? views[0]?.name ?? 'events');
  const [filters, setFilters] = useState<FilterRow[]>([]);
  const [uniqueByPath, setUniqueByPath] = useState('');
  const [uniqueOrderBy, setUniqueOrderBy] = useState<'received_at_desc' | 'received_at_asc'>('received_at_desc');
  const [schema, setSchema] = useState<SchemaField[] | null>(null);

  const [previewRows, setPreviewRows] = useState<Array<Record<string, unknown>> | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Save dialog (inline panel, not modal) — appears when operator clicks
  // "Save as view"; collects name + stream action.
  const [showSavePanel, setShowSavePanel] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [streamAction, setStreamAction] = useState<StreamAction>('none');
  const [forwardUrl, setForwardUrl] = useState('');
  const [forwardMethod, setForwardMethod] = useState<'POST' | 'PUT'>('POST');
  const [enrichUrl, setEnrichUrl] = useState('');
  const [enrichIdPath, setEnrichIdPath] = useState('');
  const [enrichMergePath, setEnrichMergePath] = useState('enrichment');
  const [dedupeWindowSec, setDedupeWindowSec] = useState(3600);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Flat shape: recipe editor builds BQ SQL projections; recursive
    // dotted paths would alias to invalid BQ identifiers.
    getEventsSchemaFlat(sourceId).then(r => setSchema(r.fields)).catch(() => setSchema([]));
  }, [sourceId]);

  // Reset baseView when views list changes (new view created).
  useEffect(() => {
    if (!views.some(v => v.name === baseView)) {
      setBaseView(views.find(v => v.name === 'events')?.name ?? views[0]?.name ?? 'events');
    }
  }, [views, baseView]);

  const hasRecipeEdits = filters.some(f => f.field_path.trim()) || uniqueByPath.trim().length > 0;

  const sql = useMemo(() => {
    if (!hasRecipeEdits) {
      // No active edits — newest-first preview of the base view.
      // received_at DESC is cheap at our scale: BQ external table must
      // read all matching GCS blobs anyway (bytes_scanned same with or
      // without ORDER BY), the sort itself is in-memory for thousands
      // of rows. P5 auto-tier promotion to native + clustering by
      // received_at unlocks the millions-of-rows case transparently.
      return `SELECT * FROM \`${dataset}.${baseView}\` ORDER BY received_at DESC`;
    }
    // Recipe SQL already wraps with WHERE / dedupe partition. Append
    // ORDER BY at the outer level so the operator's preview always shows
    // the latest matching rows first.
    return `${buildRecipeSql({ dataset, baseView, picks: [], filters, uniqueByPath, uniqueOrderBy })}\nORDER BY received_at DESC`;
  }, [dataset, baseView, filters, uniqueByPath, uniqueOrderBy, hasRecipeEdits]);

  // Bumping `refreshKey` re-triggers the preview useEffect (manual
  // refresh button click). Without this, the preview only re-runs when
  // SQL itself changes — operator can't poke the view to see new
  // events that arrived since last query.
  const [refreshKey, setRefreshKey] = useState(0);

  // Debounced preview re-fetch on SQL change OR manual refresh.
  useEffect(() => {
    setPreviewLoading(true);
    setPreviewError(null);
    const handle = setTimeout(() => {
      previewBqView(sourceId, sql, 25)
        .then(r => { setPreviewRows(r.rows); setPreviewLoading(false); })
        .catch(e => { setPreviewError(String(e)); setPreviewLoading(false); });
    }, 500);
    return () => clearTimeout(handle);
  }, [sourceId, sql, refreshKey]);

  const availableFields = (schema ?? []).filter(f => f.path !== 'payload' && f.path !== 'origin');

  function addFilter(): void { setFilters(prev => [...prev, { field_path: '', op: 'is_present', value: '' }]); }
  function updateFilter(idx: number, patch: Partial<FilterRow>): void {
    setFilters(prev => prev.map((f, i) => i === idx ? { ...f, ...patch } : f));
  }
  function removeFilter(idx: number): void { setFilters(prev => prev.filter((_, i) => i !== idx)); }
  function clearRecipe(): void {
    setFilters([]);
    setUniqueByPath('');
    setUniqueOrderBy('received_at_desc');
  }

  function buildTriggerAction(): { action: TriggerAction; needsViewScope: boolean } | null {
    if (streamAction === 'none') return null;
    if (streamAction === 'validate') {
      const fields = filters.filter(f => f.field_path.trim() && f.op === 'is_present').map(f => f.field_path.trim());
      if (fields.length === 0) {
        toast.show('Validate gate needs at least one "is present" filter row.', { kind: 'warning' });
        return null;
      }
      return { action: { type: 'validate_required', fields }, needsViewScope: false };
    }
    if (streamAction === 'dedupe') {
      if (!uniqueByPath.trim()) {
        toast.show('Dedupe gate needs the "unique on key" path set.', { kind: 'warning' });
        return null;
      }
      return { action: { type: 'validate_dedupe', key_path: uniqueByPath.trim(), window_sec: Number(dedupeWindowSec) || 3600 }, needsViewScope: false };
    }
    if (streamAction === 'forward') {
      if (!forwardUrl.trim()) {
        toast.show('Forward needs a target URL.', { kind: 'warning' });
        return null;
      }
      return { action: { type: 'forward_webhook', url: forwardUrl.trim(), method: forwardMethod }, needsViewScope: true };
    }
    if (streamAction === 'enrich') {
      if (!enrichUrl.trim() || !enrichIdPath.trim() || !enrichMergePath.trim()) {
        toast.show('Enrich needs URL, id_path, and merge_path.', { kind: 'warning' });
        return null;
      }
      return { action: { type: 'enrich_fetch', url: enrichUrl.trim(), id_path: enrichIdPath.trim(), merge_path: enrichMergePath.trim(), method: 'GET' }, needsViewScope: true };
    }
    return null;
  }

  async function handleSave(): Promise<void> {
    if (!saveName.trim()) {
      toast.show('Pick a name for the new view.', { kind: 'warning' });
      return;
    }
    const triggerSpec = streamAction === 'none' ? null : buildTriggerAction();
    if (streamAction !== 'none' && !triggerSpec) return;
    setSubmitting(true);
    try {
      await createBqView(sourceId, { name: saveName.trim(), sql });
      if (triggerSpec) {
        let viewId: string | undefined;
        if (triggerSpec.needsViewScope) {
          const tree = filterRowsToTree(filters);
          if (tree) {
            const ftView = await createView(sourceId, { name: `${saveName.trim()}__filter`, filter: tree, columns: [] });
            viewId = ftView.view_id;
          }
        }
        await createTrigger(sourceId, {
          name: `${saveName.trim()}__${streamAction}`,
          action: triggerSpec.action,
          ...(viewId ? { view_id: viewId } : {}),
        });
        toast.show(`Created view "${saveName.trim()}" + ${streamAction} trigger.`, { kind: 'success' });
      } else {
        toast.show(`Created view "${saveName.trim()}".`, { kind: 'success' });
      }
      setShowSavePanel(false);
      setSaveName('');
      setStreamAction('none');
      clearRecipe();
      onSaved();
    } catch (e) {
      toast.show(`Save failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* View tabs — each saved view is a tab. Click switches base view
          AND clears the active recipe so the operator sees the chosen
          view's rows clean. Recipe edits compose ON TOP of the active
          tab; "Save as view" creates a new tab. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, borderBottom: '1px solid var(--line)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 0, flex: 1, overflowX: 'auto', overflowY: 'hidden' }}>
          {views.map(v => {
            const active = baseView === v.name;
            return (
              <button
                key={v.name}
                type="button"
                onClick={() => { setBaseView(v.name); clearRecipe(); }}
                title={v.description ? `${v.name} — ${v.description}` : v.name}
                style={{
                  padding: '8px 14px', fontSize: 12.5, fontFamily: 'var(--mono)',
                  background: active ? 'var(--bg-elev)' : 'transparent',
                  color: active ? 'var(--ink)' : 'var(--ink-3)',
                  border: 'none',
                  borderTop: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
                  borderLeft: '1px solid var(--line)',
                  borderRight: '1px solid var(--line)',
                  marginBottom: -1,
                  cursor: 'pointer',
                  fontWeight: active ? 500 : 400,
                  whiteSpace: 'nowrap',
                }}
              >
                {v.name}
              </button>
            );
          })}
        </div>
        {/* Refresh icon — re-runs the current SQL preview without
            requiring a recipe change. Useful for "show me new events
            that arrived since the last query." */}
        <button
          type="button"
          onClick={() => setRefreshKey(k => k + 1)}
          disabled={previewLoading}
          title="Refresh — re-query the view to pick up new events"
          aria-label="Refresh view"
          style={{
            padding: '6px 10px', marginLeft: 8,
            background: 'transparent', border: '1px solid var(--line)', borderRadius: 4,
            color: previewLoading ? 'var(--ink-3)' : 'var(--ink-2)',
            cursor: previewLoading ? 'wait' : 'pointer',
            fontSize: 14, lineHeight: 1,
          }}
        >
          <span style={{
            display: 'inline-block',
            transform: previewLoading ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.4s ease',
          }}>↻</span>
        </button>
      </div>

      {/* Recipe + actions toolbar — sits below the tabs row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={addFilter}
          className="btn sm ghost"
          style={{ padding: '4px 10px', fontSize: 11.5 }}
        >+ filter</button>
        {hasRecipeEdits && (
          <button
            type="button"
            onClick={clearRecipe}
            className="btn sm ghost"
            style={{ padding: '4px 10px', fontSize: 11.5, color: 'var(--ink-3)' }}
          >clear recipe</button>
        )}
        <span style={{ flex: 1 }} />
        {hasRecipeEdits && (
          <button
            type="button"
            onClick={() => { setSaveName(''); setShowSavePanel(true); }}
            className="btn sm primary"
            style={{ padding: '4px 12px', fontSize: 12 }}
          >Save as view…</button>
        )}
      </div>

      {/* Filter rows — inline AND-joined predicate editor */}
      {filters.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '8px 10px', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 6 }}>
          {filters.map((f, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)', minWidth: 32 }}>{idx === 0 ? 'WHERE' : 'AND'}</span>
              <input
                value={f.field_path}
                onChange={e => updateFilter(idx, { field_path: e.target.value })}
                placeholder="payload field path"
                style={{ ...inputStyle, flex: 1 }}
                list={`schema-${idx}`}
              />
              <datalist id={`schema-${idx}`}>
                {availableFields.map(s => <option key={s.path} value={s.path} />)}
              </datalist>
              <select
                value={f.op}
                onChange={e => updateFilter(idx, { op: e.target.value as FilterRow['op'] })}
                style={inputStyle}
              >
                <option value="is_present">is present</option>
                <option value="is_empty">is empty</option>
                <option value="eq">=</option>
                <option value="neq">!=</option>
                <option value="gt">&gt;</option>
                <option value="lt">&lt;</option>
                <option value="contains">contains</option>
              </select>
              {!['is_present', 'is_empty'].includes(f.op) && (
                <input
                  value={f.value}
                  onChange={e => updateFilter(idx, { value: e.target.value })}
                  placeholder="value"
                  style={{ ...inputStyle, flex: 1 }}
                />
              )}
              <button
                type="button"
                onClick={() => removeFilter(idx)}
                style={{ background: 'transparent', border: '1px solid var(--line)', borderRadius: 4, padding: '5px 8px', cursor: 'pointer', color: 'var(--ink-3)', fontFamily: 'var(--mono)', fontSize: 12 }}
              >×</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, color: 'var(--ink-3)', minWidth: 96 }}>UNIQUE BY</span>
            <input
              value={uniqueByPath}
              onChange={e => setUniqueByPath(e.target.value)}
              placeholder="(optional) payload key path for last-wins dedupe"
              style={{ ...inputStyle, flex: 1 }}
              list="unique-schema"
            />
            <datalist id="unique-schema">
              {availableFields.map(s => <option key={s.path} value={s.path} />)}
            </datalist>
            <select
              value={uniqueOrderBy}
              onChange={e => setUniqueOrderBy(e.target.value as 'received_at_desc' | 'received_at_asc')}
              style={inputStyle}
              disabled={!uniqueByPath.trim()}
            >
              <option value="received_at_desc">last wins</option>
              <option value="received_at_asc">first wins</option>
            </select>
          </div>
        </div>
      )}

      {/* Live row table — debounced preview of current SQL */}
      <div style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ padding: '6px 10px', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid var(--line)', background: 'var(--bg-sunken)', fontSize: 11, color: 'var(--ink-3)' }}>
          <code style={{ fontFamily: 'var(--mono)' }}>{previewLoading ? 'refreshing…' : previewRows ? `${previewRows.length} row${previewRows.length === 1 ? '' : 's'}` : ''}</code>
          {previewError && <span style={{ color: 'var(--danger)' }}>· {previewError.slice(0, 100)}</span>}
          <span style={{ flex: 1 }} />
          <span style={{ fontFamily: 'var(--mono)' }} title={sql}>SQL: {sql.length > 120 ? sql.slice(0, 117) + '…' : sql}</span>
        </div>
        {previewRows && previewRows.length > 0 && (
          <div style={{ overflow: 'auto', maxHeight: 480 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--mono)', fontSize: 11 }}>
              <thead style={{ background: 'var(--bg-sunken)', position: 'sticky', top: 0 }}>
                <tr>
                  {Object.keys(previewRows[0] ?? {}).map(c => <th key={c} style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '1px solid var(--line)', fontWeight: 500, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--line)' }}>
                    {Object.keys(previewRows[0] ?? {}).map(c => (
                      <td key={c} style={{ padding: '6px 10px', color: 'var(--ink)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {formatCell(row[c])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {previewRows && previewRows.length === 0 && !previewError && (
          <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--ink-3)' }}>
            No rows match this recipe yet. {hasRecipeEdits ? 'Try clearing or relaxing the filters.' : 'Send an event to the source to see data here.'}
          </div>
        )}
      </div>

      {/* Save panel (inline, not modal) — appears when operator confirms
          Save as view. Lets them name the view + optionally bind a
          stream-time trigger that fires on matching ingest events. */}
      {showSavePanel && (
        <div style={{ padding: '12px 14px', background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 12.5, color: 'var(--ink)', fontWeight: 500 }}>
            Save current recipe as a view
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              value={saveName}
              onChange={e => setSaveName(e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
              placeholder="recent_orders · letters/digits/underscore"
              style={{ ...inputStyle, flex: 1 }}
              autoFocus
            />
          </div>
          <div>
            <div style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 4 }}>Also apply at ingest</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {([
                { id: 'none', label: 'View only' },
                { id: 'validate', label: 'Validate gate' },
                { id: 'dedupe', label: 'Dedupe gate' },
                { id: 'forward', label: 'Forward (POST)' },
                { id: 'enrich', label: 'Enrich' },
              ] as const).map(opt => {
                const active = streamAction === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setStreamAction(opt.id)}
                    style={{
                      padding: '4px 10px', fontSize: 11.5,
                      background: active ? 'var(--accent)' : 'var(--bg)',
                      color: active ? 'white' : 'var(--ink-2)',
                      border: `1px solid ${active ? 'var(--accent)' : 'var(--line)'}`,
                      borderRadius: 14, cursor: 'pointer',
                      fontWeight: active ? 500 : 400,
                    }}
                  >{opt.label}</button>
                );
              })}
            </div>
            {streamAction === 'forward' && (
              <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                <input value={forwardUrl} onChange={e => setForwardUrl(e.target.value)} placeholder="https://example.com/webhook" style={{ ...inputStyle, flex: 1 }} />
                <select value={forwardMethod} onChange={e => setForwardMethod(e.target.value as 'POST' | 'PUT')} style={inputStyle}>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                </select>
              </div>
            )}
            {streamAction === 'enrich' && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <input value={enrichUrl} onChange={e => setEnrichUrl(e.target.value)} placeholder="https://api.example.com/users/{id}" style={inputStyle} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <input value={enrichIdPath} onChange={e => setEnrichIdPath(e.target.value)} placeholder="id_path · e.g. customer_id" style={{ ...inputStyle, flex: 1 }} />
                  <input value={enrichMergePath} onChange={e => setEnrichMergePath(e.target.value)} placeholder="merge_path · e.g. customer" style={{ ...inputStyle, flex: 1 }} />
                </div>
              </div>
            )}
            {streamAction === 'dedupe' && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)', minWidth: 86 }}>Window (sec)</span>
                <input
                  type="number" min={1} max={604800}
                  value={dedupeWindowSec}
                  onChange={e => setDedupeWindowSec(Number(e.target.value) || 3600)}
                  style={inputStyle}
                />
                <span style={{ fontSize: 11, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>(default 3600 = 1h)</span>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn sm ghost" onClick={() => setShowSavePanel(false)} disabled={submitting}>Cancel</button>
            <button
              className="btn sm primary"
              onClick={() => { void handleSave(); }}
              disabled={submitting || !saveName.trim()}
            >
              {submitting ? 'Saving…' : streamAction === 'none' ? 'Save view' : `Save view + ${streamAction} trigger`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function formatCell(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v.length > 120 ? v.slice(0, 117) + '…' : v;
  if (typeof v === 'object') return JSON.stringify(v).slice(0, 120);
  return String(v);
}
